/**
 * cosmosPipeline — 3D volumetric nebula with stars and lens flare.
 *
 * A real 3D raymarched volumetric cloud field (FBM of FBM).
 * Combined with procedural star field and post-process lens flare.
 * Audio drives the nebula density and color.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface CosmosPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;


float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

// 3D noise for volumetric raymarching.
float noise3D(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i.xy + i.z * 17.0);
  float b = hash21(i.xy + vec2(1, 0) + i.z * 17.0);
  float c = hash21(i.xy + vec2(0, 1) + i.z * 17.0);
  float d = hash21(i.xy + vec2(1, 1) + i.z * 17.0);
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm3D(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise3D(p);
    p = p * 2.0 + vec3(1.7, 9.2, 5.3);
    a *= 0.5;
  }
  return v;
}

// Density at a 3D point in the nebula.
float nebulaDensity(vec3 p) {
  // Rotate slowly.
  float a = uTime * 0.05;
  float ca = cos(a), sa = sin(a);
  p.xz = mat2(ca, -sa, sa, ca) * p.xz;
  // Audio boost.
  float intensity = 0.6 + bass * 0.4;
  return fbm3D(p * 0.5 + vec3(uTime * 0.05, 0.0, uTime * 0.03)) * intensity;
}



void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  // Background: deep space.
  vec3 col = vec3(0.005, 0.005, 0.015);

  // Stars: tiny bright dots from hash.
  vec2 starUv = floor(gl_FragCoord.xy / 2.0);
  float starSeed = hash21(starUv);
  float starMask = step(0.9985, starSeed);
  float starBright = hash21(starUv + 1.7);
  col += vec3(0.9, 0.95, 1.0) * starMask * (0.3 + 0.7 * starBright);

  // Volumetric nebula: simple ray-march through 3D space.
  // Camera at (0, 0, 1) looking at (0, 0, 0).
  vec3 ro = vec3(0.0, 0.0, 1.0);
  vec3 rd = normalize(vec3(p * 0.6, -1.0));
  vec3 accumColor = vec3(0.0);
  float accumDensity = 0.0;
  const int STEPS = 32;
  for (int i = 0; i < STEPS; i++) {
    float t = float(i) * 0.06;
    vec3 p3 = ro + rd * t;
    float d = nebulaDensity(p3);
    // Nebula color: shift with audio.
    vec3 nebulaColor = vec3(0.5, 0.3, 0.95);
    nebulaColor = mix(nebulaColor, vec3(0.95, 0.4, 0.6), mid);
    nebulaColor = mix(nebulaColor, vec3(0.4, 0.85, 0.95), high);
    accumColor += nebulaColor * d * 0.08;
    accumDensity += d * 0.06;
  }
  // Apply nebula with fade.
  float nebulaFade = 1.0 - exp(-accumDensity);
  col = mix(col, accumColor, nebulaFade);

  // Lens flare: bright glow at the center.
  float centerDist = length(p);
  float flare = exp(-centerDist * 5.0) * (0.5 + high * 0.5);
  col += vec3(1.0, 0.9, 0.7) * flare * 0.4;

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.4;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeCosmosBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.3);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.5 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.8 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.0 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.2 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.5 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 1.9 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.3 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.8 + 3.0);
}

export function computeCosmosRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createCosmosPipeline(gl: WebGL2RenderingContext): CosmosPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawCosmos(
  gl: WebGL2RenderingContext,
  pipe: CosmosPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
